/* =========================================================
   VAYRON — SIGNATURES SHOWCASE
   ========================================================= */

(() => {

  const SECTION_SELECTOR =
    '[data-vss-section]';


  /* =========================================================
     FORMAT NUMBER
     ========================================================= */

  const formatNumber = (number) => {

    return String(number)
      .padStart(2, '0');

  };


  /* =========================================================
     INITIALIZE SECTION
     ========================================================= */

  function initSection(section) {

    if (!section) return;


    if (
      section.dataset.vssReady === 'true'
    ) {
      return;
    }


    section.dataset.vssReady = 'true';


    const track =
      section.querySelector(
        '[data-vss-track]'
      );


    const cards =
      Array.from(
        section.querySelectorAll(
          '[data-vss-card]'
        )
      );


    const progressFill =
      section.querySelector(
        '[data-vss-progress]'
      );


    const currentNumber =
      section.querySelector(
        '[data-vss-current]'
      );


    if (!track) return;


    /* =====================================================
       CARD GALLERIES
       ===================================================== */

    const galleries =
      Array.from(
        section.querySelectorAll(
          '[data-vss-gallery]'
        )
      );


    galleries.forEach(
      (gallery) => {

        const slides =
          Array.from(
            gallery.querySelectorAll(
              '[data-vss-slide]'
            )
          );


        const prev =
          gallery.querySelector(
            '[data-vss-gallery-prev]'
          );


        const next =
          gallery.querySelector(
            '[data-vss-gallery-next]'
          );


        const current =
          gallery.querySelector(
            '[data-vss-gallery-current]'
          );


        if (slides.length <= 1) {
          return;
        }


        let index = 0;


        const showSlide = (
          newIndex
        ) => {

          if (newIndex < 0) {

            index =
              slides.length - 1;

          } else if (
            newIndex >= slides.length
          ) {

            index = 0;

          } else {

            index = newIndex;

          }


          slides.forEach(
            (slide, slideIndex) => {

              const active =
                slideIndex === index;


              slide.classList.toggle(
                'is-active',
                active
              );


              slide.setAttribute(
                'aria-hidden',
                active
                  ? 'false'
                  : 'true'
              );

            }
          );


          if (current) {

            current.textContent =
              formatNumber(
                index + 1
              );

          }

        };


        if (prev) {

          prev.addEventListener(
            'click',
            (event) => {

              event.preventDefault();
              event.stopPropagation();

              showSlide(
                index - 1
              );

            }
          );

        }


        if (next) {

          next.addEventListener(
            'click',
            (event) => {

              event.preventDefault();
              event.stopPropagation();

              showSlide(
                index + 1
              );

            }
          );

        }


        showSlide(0);

      }
    );


    /* =====================================================
       TRACK HELPERS
       ===================================================== */

    const getGap = () => {

      const styles =
        window.getComputedStyle(
          track
        );


      return (
        parseFloat(
          styles.gap ||
          styles.columnGap ||
          0
        ) || 0
      );

    };


    const getStep = () => {

      const firstCard =
        cards[0];


      if (!firstCard) {
        return track.clientWidth;
      }


      return (
        firstCard
          .getBoundingClientRect()
          .width +
        getGap()
      );

    };


    const getMaxScroll = () => {

      return Math.max(
        0,
        track.scrollWidth -
        track.clientWidth
      );

    };


    /* =====================================================
       MAIN PROGRESS
       ===================================================== */

    const updateProgress = () => {

      const maxScroll =
        getMaxScroll();


      const ratio =
        maxScroll > 0
          ? track.scrollLeft /
            maxScroll
          : 0;


      if (progressFill) {

        const minimum =
          maxScroll > 0
            ? 7
            : 100;


        progressFill.style.width =
          `${Math.min(
            100,
            Math.max(
              minimum,
              ratio * 100
            )
          )}%`;

      }


      if (
        currentNumber &&
        cards.length > 0
      ) {

        const step =
          getStep();


        const index =
          step > 0
            ? Math.min(
                cards.length - 1,
                Math.max(
                  0,
                  Math.round(
                    track.scrollLeft /
                    step
                  )
                )
              )
            : 0;


        currentNumber.textContent =
          formatNumber(
            index + 1
          );

      }

    };


    /* =====================================================
       DESKTOP DRAG
       ===================================================== */

    const DRAG_THRESHOLD = 8;

    let pointerActive = false;

    let dragging = false;

    let suppressClick = false;

    let pointerId = null;

    let startX = 0;

    let startScrollLeft = 0;


    track.addEventListener(
      'pointerdown',
      (event) => {

        /*
         * Touch keeps native horizontal scroll.
         */

        if (
          event.pointerType === 'touch'
        ) {
          return;
        }


        /*
         * Do not begin card-rail drag when clicking
         * an image-navigation arrow.
         */

        if (
          event.target.closest(
            '[data-vss-gallery-prev], [data-vss-gallery-next]'
          )
        ) {
          return;
        }


        if (event.button !== 0) {
          return;
        }


        pointerActive = true;

        dragging = false;

        suppressClick = false;

        pointerId =
          event.pointerId;

        startX =
          event.clientX;

        startScrollLeft =
          track.scrollLeft;

      }
    );


    track.addEventListener(
      'pointermove',
      (event) => {

        if (!pointerActive) {
          return;
        }


        const distance =
          event.clientX -
          startX;


        if (
          !dragging &&
          Math.abs(distance) >
            DRAG_THRESHOLD
        ) {

          dragging = true;

          suppressClick = true;


          track.classList.add(
            'is-dragging'
          );


          try {

            track.setPointerCapture(
              pointerId
            );

          } catch (error) {

            /* no action */

          }

        }


        if (!dragging) {
          return;
        }


        event.preventDefault();


        track.scrollLeft =
          startScrollLeft -
          distance;

      }
    );


    /* =====================================================
       STOP DRAG
       ===================================================== */

    const stopDrag = () => {

      if (!pointerActive) {
        return;
      }


      const wasDragging =
        dragging;


      pointerActive = false;

      dragging = false;


      track.classList.remove(
        'is-dragging'
      );


      if (
        wasDragging &&
        pointerId !== null
      ) {

        try {

          if (
            track.hasPointerCapture(
              pointerId
            )
          ) {

            track.releasePointerCapture(
              pointerId
            );

          }

        } catch (error) {

          /* no action */

        }

      }


      pointerId = null;


      if (wasDragging) {

        window.setTimeout(
          () => {

            suppressClick = false;

          },
          0
        );

      }

    };


    track.addEventListener(
      'pointerup',
      stopDrag
    );


    track.addEventListener(
      'pointercancel',
      stopDrag
    );


    track.addEventListener(
      'pointerleave',
      () => {

        if (
          pointerActive &&
          !dragging
        ) {

          pointerActive = false;

          pointerId = null;

        }

      }
    );


    /* =====================================================
       PREVENT LINK AFTER REAL DRAG
       ===================================================== */

    track.addEventListener(
      'click',
      (event) => {

        if (!suppressClick) {
          return;
        }


        const link =
          event.target.closest('a');


        if (link) {

          event.preventDefault();

          event.stopPropagation();

        }


        suppressClick = false;

      },
      true
    );


    /* =====================================================
       DISABLE NATIVE IMAGE DRAG
       ===================================================== */

    track.addEventListener(
      'dragstart',
      (event) => {

        event.preventDefault();

      }
    );


    /* =====================================================
       SCROLL
       ===================================================== */

    let scrollFrame = null;


    track.addEventListener(
      'scroll',
      () => {

        if (scrollFrame) {
          return;
        }


        scrollFrame =
          window.requestAnimationFrame(
            () => {

              updateProgress();

              scrollFrame = null;

            }
          );

      },
      {
        passive: true
      }
    );


    /* =====================================================
       RESIZE
       ===================================================== */

    let resizeTimer = null;


    window.addEventListener(
      'resize',
      () => {

        window.clearTimeout(
          resizeTimer
        );


        resizeTimer =
          window.setTimeout(
            () => {

              updateProgress();

            },
            100
          );

      }
    );


    /* =====================================================
       INITIAL STATE
       ===================================================== */

    updateProgress();

  }


  /* =========================================================
     INIT ALL
     ========================================================= */

  function initAll(
    scope = document
  ) {

    if (
      scope.matches &&
      scope.matches(
        SECTION_SELECTOR
      )
    ) {

      initSection(scope);

    }


    scope
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach(
        initSection
      );

  }


  /* =========================================================
     INITIAL LOAD
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        initAll();

      }
    );

  } else {

    initAll();

  }


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initAll(
        event.target
      );

    }
  );

})();